import { Fragment, ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';

// Labels for the first path segment. Anything absent (or a deeper segment, e.g.
// an id) falls back to a prettified version of the segment itself, so new routes
// still render a sensible crumb without being registered here.
const SEGMENT_LABELS: Record<string, string> = {
  'hidden-gems': 'Hidden Gems',
  challenges: 'Challenges',
  campaigns: 'Campaigns',
  'hybrid-ledger': 'Review Video Submissions',
  users: 'Users',
  submissions: 'Submissions',
  notifications: 'Notifications',
  'coach-guide': 'Coach Guide',
  feedback: 'Support Tickets',
  analytics: 'Analytics',
  revenue: 'Revenue',
  'tap-data': 'TAP Data',
  'cap-data': 'CAP Data',
  'cap-applications': 'CAP Applications',
  'weekly-top-100': 'Weekly Top 100',
  'brand-intelligence': 'Brand Intelligence',
  'brief-requests': 'Brief Requests',
  'audit-log': 'Audit Log',
  'brand-activity': 'Brand Activity',
  'roster-campaigns': 'Jobs',
  'roster-creators': 'Creator Profile',
  'creator-payments': 'Creator Payments',
  'campaign-ops': 'Job Operations',
  'sample-requests': 'Sample Requests',
  hiring: 'Exclusive Campaigns',
  hires: 'Hire',
  batches: 'Payout Batches',
  leaderboard: 'Leaderboard',
};

const prettify = (segment: string) =>
  // A bare id shouldn't be title-cased into noise — label it generically.
  /^[0-9a-f-]{16,}$/i.test(segment) || /^\d+$/.test(segment)
    ? 'Detail'
    : segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function useCrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [{ label: 'Dashboard', href: null }];

  return segments.map((segment, i) => ({
    label: SEGMENT_LABELS[segment] ?? prettify(segment),
    // Last crumb is the current page; earlier ones link back up the path.
    href: i === segments.length - 1 ? null : '/' + segments.slice(0, i + 1).join('/'),
  }));
}

export function Layout({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const crumbs = useCrumbs();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <SidebarProvider className="min-h-svh">
      <div className="min-h-svh flex w-full">
        <AppSidebar />
        <main className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          {/* Sticky + translucent: the sidebar toggle and sign-out stay reachable
              while a long table scrolls underneath. */}
          <header className="sticky top-0 z-20 h-14 shrink-0 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
            <div className="flex h-full items-center gap-2 px-4">
              <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
              <Separator orientation="vertical" className="mr-1 h-5" />
              <Breadcrumb>
                <BreadcrumbList>
                  {crumbs.map((crumb, i) => (
                    // Separator is a sibling <li>, never nested inside the item.
                    <Fragment key={`${crumb.label}-${i}`}>
                      <BreadcrumbItem>
                        {crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link to={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage className="font-semibold text-foreground">
                            {crumb.label}
                          </BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                      {crumb.href && <BreadcrumbSeparator />}
                    </Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleLogout}
                      aria-label="Sign out"
                      className="ml-auto bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Sign out</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </header>
          {/* Tighter gutters on a phone — p-6 costs 48px of a 390px screen. */}
          <div className="flex-1 p-4 sm:p-6">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
